import { FC, useEffect, useRef, useState } from "react";
import { Button, Input, Space } from "antd";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";

import { FlexibleAnalysisOutput } from "../../../../types/ProjectTypes";
import { IFrameRenderer } from "../../../../common/components/IFrameRenderer";

interface FlexibleContentTabProps {
    output: FlexibleAnalysisOutput;
    onChange: (output: FlexibleAnalysisOutput) => void;
}

export const FlexibleContentTab: FC<FlexibleContentTabProps> = ({ output, onChange }) => {
    const [preview, setPreview] = useState(false);
    const textareaWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = textareaWrapperRef.current;
        if (!el) return;

        const handleDragOver = (e: DragEvent) => {
            e.preventDefault();

            if (e.dataTransfer) {
                e.dataTransfer.dropEffect = "copy";
            }
        };

        const handleDrop = (e: DragEvent) => {
            e.preventDefault();

            const file = e.dataTransfer?.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = function (e: ProgressEvent) {
                if (typeof reader.result === "string") {
                    onChange({ ...output, content: reader.result });
                }
            };
            reader.readAsText(file, "UTF-8");
        };

        el.addEventListener("dragover", handleDragOver);
        el.addEventListener("drop", handleDrop);

        return () => {
            el.removeEventListener("drop", handleDrop);
            el.removeEventListener("dragover", handleDragOver);
        };
    }, [textareaWrapperRef]);

    return (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                    size="small"
                    icon={preview ? <EditOutlined /> : <EyeOutlined />}
                    onClick={() => setPreview(!preview)}
                >
                    {preview ? "Edit" : "Preview"}
                </Button>
            </div>

            {preview ? (
                <div
                    style={{
                        border: "1px solid #d9d9d9",
                        height: 312,
                    }}
                >
                    <IFrameRenderer htmlContent={output.content} />
                </div>
            ) : (
                <div ref={textareaWrapperRef}>
                    <Input.TextArea
                        rows={16}
                        value={output.content || ""}
                        onChange={(e) => onChange({ ...output, content: e.target.value })}
                        placeholder="Paste HTML content here..."
                        style={{ fontFamily: "monospace", fontSize: 12 }}
                    />
                </div>
            )}
        </Space>
    );
};
